import { useContext, useEffect, useMemo } from 'react';
import Error from '../components/ErrorBoundary';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import ProductCard from '../components/ProductCard';
import SortView from '../components/filters/SortView';
import SearchFiltersOffcanvas from '../components/filters/SearchFiltersOffcanvas';
import SearchFiltersSidebar from '../components/filters/SearchFiltersSidebar';
import AppliedFilters from '../components/filters/AppliedFilters';
import { CatalogStateContext, CatalogDispatchContext } from '../store/CatalogContext';
import API from '../services/apiService';
import useBrand from '../hooks/useBrand';
import { useSearchParams } from 'react-router';
import type { Product } from '../types/product';
import useSearchAndFilter from '../hooks/useSearchAndFilter';
import ProductCardSkeletonGroup from '../components/loading-skeletons/ProductCardSkeletonGroup';
import cookies from '../utils/cookies';

const FullCatalog = () => {
  const brand = useBrand();
  const host = brand?.host;

  const [searchParams] = useSearchParams();
  const { tp, cc, gid } = Object.fromEntries([...searchParams]);

  const catalogState = useContext(CatalogStateContext);
  const catalogDispatch = useContext(CatalogDispatchContext);

  const fullCatalogProducts = useMemo(() => catalogState.products.fullCatalog || [], [catalogState.products.fullCatalog]);

  // view state from context
  const {
    pageNumber,
    productPerPage,
    sort,
    filters: { searchQuery, collection, category, subCategory, individualBundle },
    grid: currentGrid,
  } = catalogState.view.fullCatalog;

  // fetch products for the catalog
  useEffect(() => {
    // Only fetch if catalog is not already loaded
    // we dont want to call the API again if the products are already loaded
    if (fullCatalogProducts?.length) return;

    const controller = new AbortController();

    catalogDispatch({
      type: 'UPDATE',
      payload: {
        isLoading: true,
      },
    });

    const params = {
      host: host || 'certus.com',
      testPrice: tp,
      groupId: gid,
      coupon: cc,
      // fullSiteBuild: true, // default is true
      // includeUpsells: true,
    };

    API.getProducts(params, controller.signal)
      .then((res) => {
        // console.log(res.data);
        const fullCatalog = res.data as Product[];

        // setting test price cookie if it is applied to any product
        const isTestPriceApplied = fullCatalog.some(
          (product) => product?.pricing?.typePricing?.toLowerCase() === tp?.toLowerCase()
        );
        if (tp && isTestPriceApplied) {
          cookies.set('_up4tp', tp.toLowerCase(), 30);
        }

        // if queryGroupId or defaultGroupId is used on any product, set it as a cookie
        const isGroupIdApplied = fullCatalog.some(
          (product) => product.pricing.groupId?.toLowerCase() === gid?.toLowerCase()
        );
        if (gid && isGroupIdApplied) {
          cookies.set('_up4gid', gid.toLowerCase(), 30);
        }

        // if coupon is applied to any product, set it as a cookie
        const isCouponApplied = fullCatalog.some((product) => product.pricing.discountSource === 'Coupon');
        if (cc && isCouponApplied) {
          cookies.set('_up4cc', cc.toLowerCase(), 30);
        }

        catalogDispatch({
          type: 'UPDATE_PRODUCTS',
          payload: {
            fullCatalog: [...fullCatalog],
          },
        });
      })
      .catch((err) => {
        if (err?.name === 'CanceledError') {
          console.error('Request Aborted');
        } else if (err?.name === 'AxiosError') {
          // console.error(err);
          console.error(err?.response?.data);
        } else {
          console.error(err);
        }

        // show error message or reload the page
        if (err?.name !== 'CanceledError') {
          catalogDispatch({
            type: 'ERROR',
            payload: {
              isLoading: false,
              error: err?.response?.data || 'General Error',
            },
          });
        }
      });

    return () => controller.abort();
  }, [fullCatalogProducts, catalogDispatch, host, tp, cc, gid]);

  // search results, this needs to be the top level set of products
  // const { results: searchedProducts } = useFuzzySearch(fullCatalogProducts, searchQuery ? searchQuery : '');

  // use filtered products hooks to get the final product list
  const { results: resultProducts, availableFilters } = useSearchAndFilter(fullCatalogProducts, {
    searchQuery,
    collection,
    category,
    subCategory,
    individualBundle,
    sort,
  });

  const fullCatalogFilters = useMemo(
    () => ({
      collection: availableFilters.collection,
      category: availableFilters.category,
      subCategory: availableFilters.subCategory,
      individualBundle: availableFilters.individualBundle,
      showBundleFilters:
        availableFilters.individualBundle.length === 2 &&
        availableFilters.individualBundle.every((filter) => filter.matching > 0),
    }),
    [availableFilters]
  );

  // this is used to populate the filter options
  // will be dependent on the searched products
  // const fullCatalogFilters = useMemo(() => {
  //   const sets = filteredSortedProducts.reduce(
  //     (acc, p) => {
  //       if (p.productCategory) {
  //         acc.collection.add(p.productCategory.verticalName);
  //         acc.category.add(p.productCategory.categoryName);
  //         acc.subCategory.add(p.productCategory.subCategoryName);
  //       }
  //       return acc;
  //     },
  //     {
  //       collection: new Set<string>(),
  //       category: new Set<string>(),
  //       subCategory: new Set<string>(),
  //     }
  //   );
  //   return {
  //     collection: Array.from(sets.collection).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  //     category: Array.from(sets.category).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  //     subCategory: Array.from(sets.subCategory).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  //     hasBundle: hasBundle,
  //   };
  // }, [filteredSortedProducts]);

  // paginate after sorting
  const pageProducts = useMemo(() => {
    const start = (pageNumber - 1) * productPerPage;
    return resultProducts.slice(start, start + productPerPage);
  }, [resultProducts, pageNumber, productPerPage]);

  // memoize mapping to avoid remapping unless slice changes
  const productCards = useMemo(
    () => pageProducts.map((product: Product) => <ProductCard key={product.id} product={product} />),
    [pageProducts]
  );

  return (
    <>
      <title>Full Catalog</title>
      <meta name="description" content="Certus full product catalog of all available items." />

      <div className="container-fluid but-fixed py-3 py-md-4 py-lg-4 pb-lg-5">
        <div className="row justify-content-center">
          {/* Scroll sentinel for pagination resets */}
          <div id="fullCatalog-top" aria-hidden="true" />

          <Navbar activePage="full-catalog" />

          <div className="col-12 all-products">
            <div className="row gx-2 mb-3 mb-lg-4 flex align-items-center">
              <div className="col-12 col-lg-9 d-none d-lg-block">
                <h1 className="fs-4 text-theme-success visually-hidden">Full Product Catalog</h1>
                <p className="mb-0">
                  Use the search and filters below to find the product/course that's right for you or your team.
                </p>
              </div>
              <SearchFiltersOffcanvas
                group="fullCatalog"
                prefix="slide-fullCatalog"
                filters={fullCatalogFilters}
                filteredProducts={resultProducts}
              />
              <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-none d-sm-block">
                <SortView group="fullCatalog" prefix="sort-fullCatalog" />
              </div>
            </div>

            <AppliedFilters group="fullCatalog" />

            <div className="row">
              <div className="col-3 d-none d-lg-block mb-3 sticky-top">
                <div className="sticky-sidebar sticky-top">
                  <h2 className="fs-5 text-theme-success mb-3">Search and Filters</h2>
                  <SearchFiltersSidebar
                    group="fullCatalog"
                    prefix="sidebar-fullCatalog"
                    filters={fullCatalogFilters}
                    filteredProducts={resultProducts}
                  />
                </div>
              </div>

              <div className="col-12 col-lg-9">
                <div className={`row product-list-display full-catalog grid-col-${currentGrid} row gx-3 gx-xl-4`}>
                  {catalogState.isLoading ? (
                    <ProductCardSkeletonGroup count={9} />
                  ) : (
                    <>
                      {productCards.length > 0 ? (
                        productCards
                      ) : (
                        <div className="col-12 py-4 text-center text-muted">
                          No products found. Try clearing your filters if you have one applied.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Pagination group="fullCatalog" itemsLength={resultProducts.length} />
        </div>
      </div>
    </>
  );
};

export default FullCatalog;

export const ErrorBoundary = () => {
  return <Error fromPage="Full Catalog Page" />;
};
